<div class="builder-rep-report">

  <!-- Data-coverage caveat — these numbers head into invoicing, so lead with it. -->
  <div class="caveat" role="note">
    <mat-icon>info</mat-icon>
    <div>
      <strong>Read before quoting any number.</strong>
      "Last viewed" capture is still ramping — facade views only, panorama paused, no historical backfill.
      Until that's complete: <em>Stale</em> is effectively impossible, <em>Never viewed</em> means
      "not captured yet" (not genuinely unviewed), and the Active/Never split reflects data coverage,
      not real engagement. Treat this as an honest mirror of the field, not an engagement measure.
    </div>
  </div>

  <!-- No builder chosen -->
  <mat-card *ngIf="!selectedCompany?.id" class="hint-card">
    <mat-card-content>
      Select a <strong>builder</strong> in the company selector above to run the report.
    </mat-card-content>
  </mat-card>

  <ng-container *ngIf="selectedCompany?.id">

    <!-- Controls -->
    <mat-card class="controls-card">
      <mat-card-content>
        <div fxLayout="row wrap" fxLayoutAlign="start center" fxLayoutGap="24px">
          <div class="builder-name">
            <span class="label">Builder</span>
            <span class="value">{{ selectedCompany?.name }}</span>
          </div>

          <mat-form-field appearance="fill" class="manufacturer-select">
            <mat-label>Manufacturer</mat-label>
            <mat-select [(value)]="selectedManufacturerId" (selectionChange)="onManufacturerChange()"
              [disabled]="loadingManufacturers">
              <mat-option [value]="undefined">All partnered ({{ manufacturers.length }})</mat-option>
              <mat-option *ngFor="let m of manufacturers" [value]="m.id">{{ m.name }}</mat-option>
            </mat-select>
            <mat-hint *ngIf="loadingManufacturers">Loading partners…</mat-hint>
            <mat-hint *ngIf="!loadingManufacturers && !manufacturers.length">No partnered manufacturers found</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="fill" class="active-days">
            <mat-label>Active window (days)</mat-label>
            <input matInput type="number" min="1" [(ngModel)]="activeDays" (change)="loadReport()">
          </mat-form-field>

          <mat-spinner *ngIf="loadingReport" diameter="28"></mat-spinner>
        </div>
      </mat-card-content>
    </mat-card>

    <ng-container *ngIf="report && !loadingReport">

      <!-- Header summary -->
      <div class="report-header">
        <span>{{ report.builderName }}</span>
        <span class="sep">·</span>
        <span>{{ report.manufacturerName || 'All partnered manufacturers' }}</span>
        <span class="sep">·</span>
        <span>Active window: viewed in the last {{ report.activeDays }} days</span>
        <span class="sep">·</span>
        <span>{{ report.themeCount }} themes</span>
        <span class="sep">·</span>
        <span>Generated {{ report.generatedUtc | date: 'medium' }}</span>
      </div>

      <!-- KPI row — products by bucket -->
      <div class="kpis" fxLayout="row wrap" fxLayoutGap="20px grid">
        <div class="kpi bucket-active" fxFlex="25" fxFlex.lt-md="50">
          <div class="kpi-label">Active (live)</div>
          <div class="kpi-num">{{ productBucketCount(Bucket.Active) }}</div>
          <div class="kpi-sub">{{ productBucketPct(Bucket.Active) }}% · in a recently-viewed theme</div>
        </div>
        <div class="kpi bucket-stale" fxFlex="25" fxFlex.lt-md="50">
          <div class="kpi-label">Stale</div>
          <div class="kpi-num">{{ productBucketCount(Bucket.Stale) }}</div>
          <div class="kpi-sub">{{ productBucketPct(Bucket.Stale) }}% · theme viewed long ago</div>
        </div>
        <div class="kpi bucket-never" fxFlex="25" fxFlex.lt-md="50">
          <div class="kpi-label">Never viewed</div>
          <div class="kpi-num">{{ productBucketCount(Bucket.Never) }}</div>
          <div class="kpi-sub">{{ productBucketPct(Bucket.Never) }}% · in a theme, no views</div>
        </div>
        <div class="kpi bucket-none" fxFlex="25" fxFlex.lt-md="50">
          <div class="kpi-label">Not in a theme</div>
          <div class="kpi-num">{{ productBucketCount(Bucket.NotInTheme) }}</div>
          <div class="kpi-sub">{{ productBucketPct(Bucket.NotInTheme) }}% · catalogue only</div>
        </div>
      </div>

      <!-- Detail tables -->
      <mat-card class="tables-card">
        <mat-tab-group animationDuration="0ms">

          <!-- Products -->
          <mat-tab label="Products ({{ productRows.length }})">
            <div class="table-scroll">
              <table mat-table [dataSource]="productRows" class="rep-table">
                <ng-container matColumnDef="manufacturer">
                  <th mat-header-cell *matHeaderCellDef>Manufacturer</th>
                  <td mat-cell *matCellDef="let r">{{ r.manufacturer }}</td>
                </ng-container>
                <ng-container matColumnDef="product">
                  <th mat-header-cell *matHeaderCellDef>Product</th>
                  <td mat-cell *matCellDef="let r">{{ r.product }}</td>
                </ng-container>
                <ng-container matColumnDef="sku">
                  <th mat-header-cell *matHeaderCellDef>SKU</th>
                  <td mat-cell *matCellDef="let r" class="mono">{{ r.sku }}</td>
                </ng-container>
                <ng-container matColumnDef="themeCount">
                  <th mat-header-cell *matHeaderCellDef class="num">Themes</th>
                  <td mat-cell *matCellDef="let r" class="num">{{ r.themeCount }}</td>
                </ng-container>
                <ng-container matColumnDef="themes">
                  <th mat-header-cell *matHeaderCellDef>In themes</th>
                  <td mat-cell *matCellDef="let r" class="themes-cell">{{ r.themes }}</td>
                </ng-container>
                <ng-container matColumnDef="lastViewed">
                  <th mat-header-cell *matHeaderCellDef>Last viewed</th>
                  <td mat-cell *matCellDef="let r">{{ r.lastViewed ? (r.lastViewed | date: 'short') : '—' }}</td>
                </ng-container>
                <ng-container matColumnDef="bucket">
                  <th mat-header-cell *matHeaderCellDef>Status</th>
                  <td mat-cell *matCellDef="let r">
                    <span class="pill" [ngClass]="bucketClass(r.bucket)">{{ bucketLabel(r.bucket) }}</span>
                  </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="productColumns; sticky: true"></tr>
                <tr mat-row *matRowDef="let row; columns: productColumns;"></tr>
              </table>
            </div>
          </mat-tab>

          <!-- Themes -->
          <mat-tab label="Themes ({{ themeRows.length }})">
            <div class="table-scroll">
              <table mat-table [dataSource]="themeRows" class="rep-table">
                <ng-container matColumnDef="theme">
                  <th mat-header-cell *matHeaderCellDef>Theme</th>
                  <td mat-cell *matCellDef="let r">{{ r.theme }}</td>
                </ng-container>
                <ng-container matColumnDef="lastViewed">
                  <th mat-header-cell *matHeaderCellDef>Last viewed</th>
                  <td mat-cell *matCellDef="let r">{{ r.lastViewed ? (r.lastViewed | date: 'short') : '—' }}</td>
                </ng-container>
                <ng-container matColumnDef="bucket">
                  <th mat-header-cell *matHeaderCellDef>Status</th>
                  <td mat-cell *matCellDef="let r">
                    <span class="pill" [ngClass]="bucketClass(r.bucket)">{{ bucketLabel(r.bucket) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="products">
                  <th mat-header-cell *matHeaderCellDef class="num">Products</th>
                  <td mat-cell *matCellDef="let r" class="num">{{ r.products }}</td>
                </ng-container>
                <ng-container matColumnDef="manufacturers">
                  <th mat-header-cell *matHeaderCellDef class="num">Manufacturers</th>
                  <td mat-cell *matCellDef="let r" class="num">{{ r.manufacturers }}</td>
                </ng-container>
                <ng-container matColumnDef="represented">
                  <th mat-header-cell *matHeaderCellDef>Represented</th>
                  <td mat-cell *matCellDef="let r" class="themes-cell">{{ r.represented }}</td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="themeColumns; sticky: true"></tr>
                <tr mat-row *matRowDef="let row; columns: themeColumns;"></tr>
              </table>
            </div>
          </mat-tab>

          <!-- Manufacturer exposure -->
          <mat-tab label="Manufacturers ({{ exposureRows.length }})">
            <div class="table-scroll">
              <table mat-table [dataSource]="exposureRows" class="rep-table">
                <ng-container matColumnDef="manufacturer">
                  <th mat-header-cell *matHeaderCellDef>Manufacturer</th>
                  <td mat-cell *matCellDef="let r">{{ r.manufacturer }}</td>
                </ng-container>
                <ng-container matColumnDef="themes">
                  <th mat-header-cell *matHeaderCellDef class="num">Themes</th>
                  <td mat-cell *matCellDef="let r" class="num">{{ r.themes }}</td>
                </ng-container>
                <ng-container matColumnDef="activeThemes">
                  <th mat-header-cell *matHeaderCellDef class="num">Active themes</th>
                  <td mat-cell *matCellDef="let r" class="num">{{ r.activeThemes }}</td>
                </ng-container>
                <ng-container matColumnDef="products">
                  <th mat-header-cell *matHeaderCellDef class="num">Products</th>
                  <td mat-cell *matCellDef="let r" class="num">{{ r.products }}</td>
                </ng-container>
                <ng-container matColumnDef="lastSeen">
                  <th mat-header-cell *matHeaderCellDef>Last seen</th>
                  <td mat-cell *matCellDef="let r">{{ r.lastSeen ? (r.lastSeen | date: 'short') : '—' }}</td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="exposureColumns; sticky: true"></tr>
                <tr mat-row *matRowDef="let row; columns: exposureColumns;"></tr>
              </table>
            </div>
          </mat-tab>

        </mat-tab-group>
      </mat-card>

    </ng-container>

  </ng-container>
</div>
